import { useEffect, useState } from "react";
import { ArrowLeft, Loader2, ExternalLink, Trash2 } from "lucide-react";
import { fetchPhraseAnalysis, deletePhrase } from "@/lib/api";
import { toast } from "sonner";

// Numbers that tend to be cited heavily in decoding work — get a subtle "hot" badge.
const NOTABLE_VALUES = new Set([
  13, 17, 22, 23, 26, 33, 38, 42, 44, 45, 47, 48, 55, 56, 64, 66, 67, 71, 74,
  76, 78, 83, 87, 88, 93, 110, 111, 113, 119, 133, 137, 138, 144, 150, 155,
  166, 168, 170, 174, 196, 201, 202, 207, 211, 213, 222, 223, 232, 233, 247,
  263, 273, 283, 317, 322, 333, 411, 444, 555, 616, 617, 666, 677, 777, 911,
]);

export default function PhraseAnalysis({ id }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchPhraseAnalysis(id)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setError("Phrase not found or unreachable."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${data.phrase.text}" from the dictionary?`)) return;
    try {
      await deletePhrase(id);
      toast.success("Deleted");
      window.close();
    } catch {
      toast.error("Delete failed");
    }
  };

  const goToSearch = (value) => {
    // Open a fresh number search in another new tab.
    window.open(`/?search=${value}`, "_blank", "noopener,noreferrer");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center muted">
        <Loader2 className="animate-spin" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 muted px-6 text-center">
        <div className="font-mono text-sm">{error || "Could not load phrase."}</div>
        <a href="/" className="btn-primary px-5 py-2 text-xs">Back to Directory</a>
      </div>
    );
  }

  const { phrase, breakdown } = data;
  const big4 = breakdown.filter((b) => b.is_big4);
  const others = breakdown.filter((b) => !b.is_big4);

  return (
    <div className="min-h-screen bg-sacred" data-testid="phrase-analysis-page">
      {/* Header */}
      <header className="glass sticky top-0 z-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between gap-4 flex-wrap">
          <a href="/" className="flex items-center gap-2 muted hover:text-white text-xs font-mono uppercase tracking-[0.18em]" data-testid="back-link">
            <ArrowLeft size={14} /> Directory
          </a>
          <div className="flex items-center gap-2 muted">
            <span className="text-lg select-none" aria-hidden="true">💎</span>
            <span className="font-display text-xl"><span className="gold">Gem</span> Key</span>
            <span className="text-base select-none" aria-hidden="true">🔑</span>
          </div>
          <button onClick={handleDelete} className="btn-ghost text-xs font-mono uppercase tracking-wider px-3 py-1.5 flex items-center gap-1.5" data-testid="delete-phrase-btn">
            <Trash2 size={12} /> Delete
          </button>
        </div>
      </header>

      <main className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-14 space-y-12">
        {/* Phrase hero */}
        <section className="rise">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] muted mb-3">
            // Phrase Analysis
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight break-words" data-testid="phrase-title">
            {phrase.text}
          </h1>
          {phrase.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-4">
              {phrase.tags.map((t) => (
                <span key={t} className={`tag ${["ritual","occult","death","kill","evil","beast"].includes(t) ? "tag-hot" : ""}`}>
                  {t}
                </span>
              ))}
            </div>
          )}
          {phrase.notes && (
            <div className="mt-5 max-w-3xl px-4 py-3 bg-[rgba(212,175,55,0.06)] border-l-2 border-[var(--primary)] text-[var(--primary)] text-sm italic font-mono leading-relaxed" data-testid="phrase-notes">
              {phrase.notes}
            </div>
          )}
        </section>

        {/* Big 4 cipher cards */}
        <section className="rise rise-1 space-y-4">
          <div className="flex items-baseline justify-between">
            <h2 className="font-display text-2xl">
              <span className="gold">Big 4</span> — primary ciphers
            </h2>
            <span className="font-mono text-[10px] muted uppercase tracking-[0.18em]">
              Click any value to find every other phrase that matches
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="big4-grid">
            {big4.map((b) => (
              <CipherCard key={b.key} item={b} onClick={() => goToSearch(b.value)} />
            ))}
          </div>
        </section>

        {/* Secondary ciphers */}
        <section className="rise rise-2 space-y-4">
          <h2 className="font-display text-2xl muted">Secondary ciphers</h2>
          <div className="panel divide-y divide-white/10" data-testid="secondary-table">
            {others.map((b) => (
              <button
                key={b.key}
                onClick={() => goToSearch(b.value)}
                className="w-full text-left px-5 py-4 hover:bg-white/[0.03] flex items-center gap-5 group transition-colors"
                data-testid={`secondary-row-${b.key}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-xs uppercase tracking-wider muted">{b.label}</div>
                </div>
                <div className="text-right">
                  <div className={`font-mono text-2xl ${NOTABLE_VALUES.has(b.value) ? "gold" : "text-white"}`}>
                    {b.value}
                  </div>
                </div>
                <div className="text-right w-40">
                  <div className="font-mono text-[10px] muted uppercase tracking-wider">other matches</div>
                  <div className="font-mono text-sm">{b.total_other}</div>
                </div>
                <ExternalLink size={14} className="muted group-hover:text-[var(--primary)]" />
              </button>
            ))}
          </div>
        </section>

        <footer className="muted font-mono text-[11px] tracking-wider pt-6 border-t border-white/10">
          // Phrase ID: <span className="gold">{phrase.id}</span> • Added {new Date(phrase.created_at).toLocaleDateString()}
        </footer>
      </main>
    </div>
  );
}

function CipherCard({ item, onClick }) {
  const hot = NOTABLE_VALUES.has(item.value);
  return (
    <button
      onClick={onClick}
      className={`panel panel-hover text-left p-5 group transition-all hover:translate-y-[-2px] ${hot ? "border-[rgba(212,175,55,0.35)]" : ""}`}
      data-testid={`big4-${item.key}`}
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] muted">{item.label}</div>
      <div className={`font-mono text-5xl mt-2 ${hot ? "big-number" : "text-white"}`}>
        {item.value}
      </div>
      <div className="mt-3 flex items-center justify-between">
        <span className="font-mono text-[10px] muted uppercase tracking-wider">
          {item.total_other} other match{item.total_other === 1 ? "" : "es"}
        </span>
        <ExternalLink size={12} className="muted group-hover:text-[var(--primary)]" />
      </div>
    </button>
  );
}
